import { ImprovementGoal } from "@/types";
import { useApiData } from "@/contexts/ApiData";
import { useDateFormatter, useTranslations } from "@/contexts/Translation";
import { H2, H3 } from "../Headers/Headings";
import Section from "../Section";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { InfoItem } from "../Survey/InfoItem";
import ConclusionFlag from "./ConclusionFlag";
import SystemMessage from "../SystemMessage/Index";
import ProfileImage from "../ProfileImage";
import Card from "../Card/Card";
import styles from "./List.module.css";
import { InfoMessageBox } from "../InfoMessage/InfoMessageBox";

type SurveysProps = {
  isShared?: boolean;
  isOrgGoals?: boolean;
  onlyOngoing?: boolean;
};

const ImprovementGoals = ({ isShared = false, isOrgGoals = false, onlyOngoing = false }: SurveysProps) => {
  const t = useTranslations("improvementGoals");
  const d = useDateFormatter();
  const { get } = useApiData();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [goals, setGoals] = useState<ImprovementGoal[]>([]);

  useEffect(() => {
    const callOwn = async () => {
      const url = onlyOngoing ? "/goals?onlyOngoing=true" : "/goals";
      const goalList = await get(url);
      setGoals(goalList);
      setIsLoading(false);
    };
    const callShared = async () => {
      const goalList = await get(`/shared/goals`);
      setGoals(goalList);
      setIsLoading(false);
    };
    const callOrgGoals = async () => {
      const goalList = await get(`/organization-goal`);
      setGoals(goalList);
      setIsLoading(false);
    };
    if (isShared) {
      callShared();
    } else if (isOrgGoals) {
      callOrgGoals();
    } else {
      callOwn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyOngoing]);

  const getNavUrl = (goal: ImprovementGoal) => {
    if (isOrgGoals) {
      return `/organization/goal/${goal.id}`;
    }
    return `/improvement/${goal.id}`;
  };

  if (isLoading) {
    return <SystemMessage message='loading' />;
  }

  return (
    <Section span={8}>
      <H2>{t(isShared ? "sharedGoalsTitle" : "goalsTitle")}</H2>
      {goals.length === 0 && <InfoMessageBox body={t(isShared ? "noSharedGoals" : "noGoals")} />}
      {goals.map((goal) => {
        return (
          <Card className={styles.goalCard} variant='floating' key={goal.id} padding='none' onClick={() => navigate(getNavUrl(goal))}>
            {goal.name ? (
              <H3 variant='dense'>
                <div className={styles.goalTitleRow}>
                  <ProfileImage src={goal.profileImage ? `/files/profile/${goal.profileImage}` : ""} variant='small' alt={goal.candidate || goal.name} />
                  <div>
                    {goal.name} <ConclusionFlag isSuccess={goal.isSuccess} />
                  </div>
                </div>
              </H3>
            ) : null}
            <div className={styles.goalMetaRow}>
              <div className={styles.goalInfo}>
                <InfoItem>{t("registeredLabel", d(goal.registered || ""))}</InfoItem>
                <InfoItem>{t("endDateLabel", d(goal.endDate))}</InfoItem>
              </div>
            </div>
            {isShared && <p className={styles.sharedBy}>{t("sharedBy", goal.candidate)}</p>}
          </Card>
        );
      })}
    </Section>
  );
};

export default ImprovementGoals;
